import { useCallback, useEffect, useRef, useState } from 'react';

type UseChatScrollControlParams<TMessage> = {
  messages: TMessage[];
  isThinking: boolean;
  isExpanded: boolean;
  enableScrollToBottomControl: boolean;
};

type UseChatScrollControlResult = {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  showScrollButton: boolean;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  markUserMessageSubmitted: () => void;
  saveCurrentScrollPosition: () => void;
};

const useChatScrollControl = <TMessage>({
  messages,
  isThinking,
  isExpanded,
  enableScrollToBottomControl,
}: UseChatScrollControlParams<TMessage>): UseChatScrollControlResult => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollPosition = useRef<number>(0);
  const previousMessagesLengthRef = useRef<number>(messages.length);
  const previousIsThinkingRef = useRef<boolean>(isThinking);
  const userJustSentMessageRef = useRef<boolean>(false);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);

  const isScrolledToBottom = useCallback((el: HTMLDivElement) => {
    const threshold = 50;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollTo({ top: el.scrollHeight, behavior });
    savedScrollPosition.current = el.scrollHeight;
    setIsAtBottom(true);
    setShowScrollButton(false);
  }, []);

  const saveCurrentScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;
    savedScrollPosition.current = scrollRef.current.scrollTop;
  }, []);

  const markUserMessageSubmitted = useCallback(() => {
    userJustSentMessageRef.current = true;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isExpanded) return;

    requestAnimationFrame(() => {
      if (savedScrollPosition.current > 0) {
        el.scrollTop = savedScrollPosition.current;
      }
      const atBottom = isScrolledToBottom(el);
      setIsAtBottom(atBottom);
      setShowScrollButton(enableScrollToBottomControl && !atBottom);
    });
  }, [isExpanded, isScrolledToBottom, enableScrollToBottomControl]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isExpanded) return;

    const handleScroll = () => {
      const atBottom = isScrolledToBottom(el);
      setIsAtBottom(atBottom);
      setShowScrollButton(enableScrollToBottomControl && !atBottom);
      savedScrollPosition.current = el.scrollTop;
    };

    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isExpanded, isScrolledToBottom, enableScrollToBottomControl]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isExpanded) return;

    const messagesAdded = messages.length !== previousMessagesLengthRef.current;
    previousMessagesLengthRef.current = messages.length;
    const thinkingStarted = isThinking && !previousIsThinkingRef.current;
    previousIsThinkingRef.current = isThinking;

    if (messagesAdded || thinkingStarted) {
      if (!enableScrollToBottomControl) {
        requestAnimationFrame(() => {
          scrollToBottom('smooth');
        });
        userJustSentMessageRef.current = false;
        return;
      }

      // Only auto-scroll in direct response to a user submit.
      // Incoming assistant messages should not force the viewport to bottom.
      const shouldAutoScroll = userJustSentMessageRef.current;

      if (!shouldAutoScroll) {
        const atBottomNow = isScrolledToBottom(el);
        setIsAtBottom(atBottomNow);
        setShowScrollButton(enableScrollToBottomControl && !atBottomNow);
        return;
      }

      requestAnimationFrame(() => {
        scrollToBottom('smooth');
      });
    }
    userJustSentMessageRef.current = false;
  }, [
    messages.length,
    isThinking,
    isExpanded,
    isAtBottom,
    scrollToBottom,
    enableScrollToBottomControl,
  ]);

  return {
    scrollRef,
    showScrollButton,
    scrollToBottom,
    markUserMessageSubmitted,
    saveCurrentScrollPosition,
  };
};

export default useChatScrollControl;
